import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import DashboardLayout from '@/components/layout/DashboardLayout';
import SEOHead from '@/components/SEOHead';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Ticket, Clock, CheckCircle, AlertCircle, XCircle, Mail, User, Calendar, MessageSquare, Paperclip, Download, FileIcon, Send, Reply } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import { enUS, it, de, fr, es, nl, pt, pl, ro, bg, cs, da, el, et, fi, hr, hu, lt, lv, mt, sk, sl, sv } from 'date-fns/locale';

type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';

interface SupportTicket {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: string;
  priority: string;
  user_id: string | null;
  created_at: string;
  updated_at: string;
  attachments: string[] | null;
}

interface TicketReply {
  id: string;
  ticket_id: string;
  admin_id: string;
  message: string;
  created_at: string;
}

const localeMap: Record<string, Locale> = {
  en: enUS, it, de, fr, es, nl, pt, pl, ro, bg, cs, da, el, et, fi, hr, hu, lt, lv, mt, sk, sl, sv,
  ga: enUS // Irish fallback to English
};

export default function AdminTickets() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [replyMessage, setReplyMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const dateLocale = localeMap[i18n.language] || enUS;

  const statusConfig: Record<TicketStatus, { labelKey: string; icon: React.ElementType; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    open: { labelKey: 'adminTickets.statusOpen', icon: AlertCircle, variant: 'destructive' },
    in_progress: { labelKey: 'adminTickets.statusInProgress', icon: Clock, variant: 'default' },
    resolved: { labelKey: 'adminTickets.statusResolved', icon: CheckCircle, variant: 'secondary' },
    closed: { labelKey: 'adminTickets.statusClosed', icon: XCircle, variant: 'outline' },
  };

  const priorityConfig: Record<TicketPriority, { labelKey: string; color: string }> = {
    low: { labelKey: 'adminTickets.priorityLow', color: 'bg-slate-500' },
    normal: { labelKey: 'adminTickets.priorityNormal', color: 'bg-blue-500' },
    high: { labelKey: 'adminTickets.priorityHigh', color: 'bg-orange-500' },
    urgent: { labelKey: 'adminTickets.priorityUrgent', color: 'bg-red-500' },
  };

  const { data: tickets, isLoading } = useQuery({
    queryKey: ['admin-tickets', statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('support_tickets')
        .select('*')
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as SupportTicket[];
    },
  });

  const { data: replies } = useQuery({
    queryKey: ['ticket-replies', selectedTicket?.id],
    queryFn: async () => {
      if (!selectedTicket) return [];
      const { data, error } = await supabase
        .from('ticket_replies')
        .select('*')
        .eq('ticket_id', selectedTicket.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as TicketReply[];
    },
    enabled: !!selectedTicket,
  });

  const updateTicketMutation = useMutation({
    mutationFn: async ({ id, status, priority }: { id: string; status?: string; priority?: string }) => {
      const updates: { status?: string; priority?: string } = {};
      if (status) updates.status = status;
      if (priority) updates.priority = priority;

      const { error } = await supabase
        .from('support_tickets')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tickets'] });
      toast.success(t('adminTickets.ticketUpdated'));
    },
    onError: () => {
      toast.error(t('adminTickets.ticketUpdateError'));
    },
  });

  const handleSendReply = async () => {
    if (!selectedTicket || !replyMessage.trim()) return;

    setIsSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Not authenticated');
        return;
      }

      const response = await supabase.functions.invoke('reply-ticket', {
        body: {
          ticketId: selectedTicket.id,
          message: replyMessage,
          userEmail: selectedTicket.email,
          userName: selectedTicket.name,
          ticketSubject: selectedTicket.subject,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast.success(t('adminTickets.replySent'));
      setReplyMessage('');
      queryClient.invalidateQueries({ queryKey: ['ticket-replies', selectedTicket.id] });

      // Auto-update status to in_progress if it was open
      if (selectedTicket.status === 'open') {
        updateTicketMutation.mutate({ id: selectedTicket.id, status: 'in_progress' });
        setSelectedTicket({ ...selectedTicket, status: 'in_progress' });
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error(t('adminTickets.replyError'));
    } finally {
      setIsSending(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config = statusConfig[status as TicketStatus] || statusConfig.open;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="w-3 h-3" />
        {t(config.labelKey)}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const config = priorityConfig[priority as TicketPriority] || priorityConfig.normal;
    return (
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${config.color}`} />
        <span className="text-sm">{t(config.labelKey)}</span>
      </div>
    );
  };

  const stats = {
    total: tickets?.length || 0,
    open: tickets?.filter(t => t.status === 'open').length || 0,
    inProgress: tickets?.filter(t => t.status === 'in_progress').length || 0,
    resolved: tickets?.filter(t => t.status === 'resolved' || t.status === 'closed').length || 0,
  };

  return (
    <DashboardLayout>
      <SEOHead
        title={t('adminTickets.pageTitle')}
        description={t('adminTickets.metaDescription')}
      />

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('adminTickets.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('adminTickets.subtitle')}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('adminTickets.totalTickets')}</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Ticket className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('adminTickets.openTickets')}</p>
                  <p className="text-2xl font-bold text-destructive">{stats.open}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('adminTickets.inProgress')}</p>
                  <p className="text-2xl font-bold text-primary">{stats.inProgress}</p>
                </div>
                <Clock className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('adminTickets.resolved')}</p>
                  <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('adminTickets.supportTickets')}</CardTitle>
                <CardDescription>{t('adminTickets.supportTicketsDescription')}</CardDescription>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={t('adminTickets.filterByStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('adminTickets.all')}</SelectItem>
                  <SelectItem value="open">{t('adminTickets.open')}</SelectItem>
                  <SelectItem value="in_progress">{t('adminTickets.in_progress')}</SelectItem>
                  <SelectItem value="resolved">{t('adminTickets.statusResolved')}</SelectItem>
                  <SelectItem value="closed">{t('adminTickets.closed')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : tickets && tickets.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('adminTickets.tableDate')}</TableHead>
                    <TableHead>{t('adminTickets.tableUser')}</TableHead>
                    <TableHead>{t('adminTickets.tableSubject')}</TableHead>
                    <TableHead>{t('adminTickets.tablePriority')}</TableHead>
                    <TableHead>{t('adminTickets.tableStatus')}</TableHead>
                    <TableHead className="text-right">{t('adminTickets.tableActions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((ticket) => (
                    <TableRow key={ticket.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedTicket(ticket)}>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(ticket.created_at), 'dd MMM yyyy HH:mm', { locale: dateLocale })}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{ticket.name}</p>
                          <p className="text-sm text-muted-foreground">{ticket.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{ticket.subject}</TableCell>
                      <TableCell>{getPriorityBadge(ticket.priority)}</TableCell>
                      <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedTicket(ticket); }}>
                          {t('adminTickets.details')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t('adminTickets.noTicketsFound')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => { if (!open) { setSelectedTicket(null); setReplyMessage(''); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              {selectedTicket?.subject}
            </DialogTitle>
            <DialogDescription>
              Ticket #{selectedTicket?.id.slice(0, 8)}
            </DialogDescription>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-6">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('adminTickets.dialogName')}</span>
                  <span className="font-medium">{selectedTicket.name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('adminTickets.dialogEmail')}</span>
                  <a href={`mailto:${selectedTicket.email}`} className="font-medium text-primary hover:underline">
                    {selectedTicket.email}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('adminTickets.dialogCreated')}</span>
                  <span className="font-medium">
                    {format(new Date(selectedTicket.created_at), 'dd MMMM yyyy HH:mm', { locale: dateLocale })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('adminTickets.dialogUpdated')}</span>
                  <span className="font-medium">
                    {format(new Date(selectedTicket.updated_at), 'dd MMMM yyyy HH:mm', { locale: dateLocale })}
                  </span>
                </div>
              </div>

              {/* Original Message */}
              <div>
                <h4 className="text-sm font-medium mb-2">{t('adminTickets.dialogMessage')}</h4>
                <div className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap">
                  {selectedTicket.message}
                </div>
              </div>

              {/* Attachments */}
              {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    {t('adminTickets.dialogAttachments')} ({selectedTicket.attachments.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedTicket.attachments.map((attachment, index) => {
                      const fileName = attachment.split('/').pop() || 'file';
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-muted rounded-lg"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <FileIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm truncate">{fileName}</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const { data } = await supabase.storage
                                .from('ticket-attachments')
                                .createSignedUrl(attachment, 3600);
                              if (data?.signedUrl) {
                                window.open(data.signedUrl, '_blank');
                              }
                            }}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            {t('adminTickets.download')}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Previous Replies */}
              {replies && replies.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Reply className="w-4 h-4" />
                    {t('adminTickets.previousReplies')} ({replies.length})
                  </h4>
                  <div className="space-y-3">
                    {replies.map((reply) => (
                      <div key={reply.id} className="bg-primary/5 border-l-4 border-primary p-4 rounded-r-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-primary">{t('adminTickets.you')}</span>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(reply.created_at), 'dd MMM yyyy HH:mm', { locale: dateLocale })}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Reply Section */}
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  {t('adminTickets.replySection')}
                </h4>
                <Textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder={t('adminTickets.replyPlaceholder')}
                  className="min-h-[120px] mb-3"
                />
                <div className="flex justify-end">
                  <Button 
                    onClick={handleSendReply} 
                    disabled={!replyMessage.trim() || isSending}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {isSending ? t('adminTickets.sending') : t('adminTickets.sendReply')}
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Status/Priority Actions */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1 block">{t('adminTickets.status')}</label>
                  <Select
                    value={selectedTicket.status}
                    onValueChange={(value) => {
                      updateTicketMutation.mutate({ id: selectedTicket.id, status: value });
                      setSelectedTicket({ ...selectedTicket, status: value });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">{t('adminTickets.statusOpen')}</SelectItem>
                      <SelectItem value="in_progress">{t('adminTickets.statusInProgress')}</SelectItem>
                      <SelectItem value="resolved">{t('adminTickets.statusResolved')}</SelectItem>
                      <SelectItem value="closed">{t('adminTickets.statusClosed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1 block">{t('adminTickets.priority')}</label>
                  <Select
                    value={selectedTicket.priority}
                    onValueChange={(value) => {
                      updateTicketMutation.mutate({ id: selectedTicket.id, priority: value });
                      setSelectedTicket({ ...selectedTicket, priority: value });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">{t('adminTickets.priorityLow')}</SelectItem>
                      <SelectItem value="normal">{t('adminTickets.priorityNormal')}</SelectItem>
                      <SelectItem value="high">{t('adminTickets.priorityHigh')}</SelectItem>
                      <SelectItem value="urgent">{t('adminTickets.priorityUrgent')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button asChild variant="outline">
                  <a href={`mailto:${selectedTicket.email}?subject=Re: ${selectedTicket.subject}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    {t('adminTickets.replyViaEmail')}
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
